import { Controller } from "@hotwired/stimulus"
import { moveBody, noteRequired, reasonError } from "../lib/lost_reason_logic"

// The deals board (/deals): drag a movable card onto another stage's column
// with native HTML5 drag and drop. The card moves at once and the stage is
// saved with PATCH /deals/:id/move (JSON); the answer brings every column's
// count and amount under the board's filters. A refused or failed move puts
// the card back and says why. A drop on a lost stage (data-lost) first asks
// for the lost reason in a dialog; cancelling it puts the card back. Locked
// cards (a workspace's subscription, whose stage is the product's) are
// not draggable. Each movable card also carries
// a "Presunúť do" form, so the keyboard and a browser without JavaScript can
// do the same.
export default class extends Controller {
  static targets = ["column", "card", "status", "error", "dialog", "dialogError"]
  static values = { filters: Object, moved: String }

  start(event) {
    const card = event.currentTarget
    if (card.dataset.movable !== "true") {
      event.preventDefault()
      return
    }
    this.dragged = card
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", card.dataset.dealId)
    card.classList.add("opacity-50")
  }

  end(event) {
    event.currentTarget.classList.remove("opacity-50")
    this.columnTargets.forEach((column) => this.unmark(column))
    this.dragged = null
  }

  over(event) {
    if (!this.dragged) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    event.currentTarget.classList.add("ring-2", "ring-brand")
  }

  leave(event) {
    const column = event.currentTarget
    if (!column.contains(event.relatedTarget)) this.unmark(column)
  }

  async drop(event) {
    event.preventDefault()
    const column = event.currentTarget
    const card = this.dragged
    this.unmark(column)
    if (!card || card.closest("[data-stage-id]") === column) return

    const origin = card.parentElement
    const next = card.nextElementSibling
    column.querySelector("[data-cards]").prepend(card)
    this.refreshEmpty()
    this.hideError()

    let reason = null
    if (column.dataset.lost === "true") {
      reason = await this.askReason()
      if (!reason) {
        origin.insertBefore(card, next)
        this.refreshEmpty()
        return
      }
    }

    try {
      const response = await fetch(card.dataset.moveUrl, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfToken },
        body: JSON.stringify(moveBody(column.dataset.stageId, reason, this.filtersValue)),
        credentials: "same-origin"
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(body.error || "Obchod sa nepodarilo presunúť.")

      this.update(body, card)
    } catch (error) {
      origin.insertBefore(card, next)
      this.refreshEmpty()
      this.showError(error.message)
    }
  }

  // The columns' totals and the card's days in its new stage, from the answer.
  update(body, card) {
    Object.entries(body.columns || {}).forEach(([stageId, totals]) => {
      const column = this.columnTargets.find((item) => item.dataset.stageId === stageId)
      if (!column) return
      column.querySelector("[data-column-count]").textContent = totals.count.toLocaleString("sk-SK")
      column.querySelector("[data-column-amount]").textContent = totals.amount
    })
    const days = card.querySelector("[data-days-in-stage]")
    if (days && body.changed) days.textContent = "dnes"
    const select = card.querySelector("select[name=stage_id]")
    if (select && body.deal) select.value = String(body.deal.stage_id)
    if (this.hasStatusTarget && body.deal) this.statusTarget.textContent = `${this.movedValue} ${body.deal.stage}.`
  }

  // Opens the lost reason dialog; resolves with { lost_reason_id, lost_note }, or null when cancelled.
  askReason() {
    if (!this.hasDialogTarget) return Promise.resolve({})
    const form = this.dialogTarget.querySelector("form")
    form.reset()
    this.dialogErrorTarget.classList.add("hidden")
    this.dialogTarget.showModal()
    return new Promise((resolve) => { this.pendingReason = resolve })
  }

  submitReason(event) {
    event.preventDefault()
    const form = event.target
    const select = form.elements.lost_reason_id
    const note = form.elements.lost_note.value
    const message = reasonError(select.value, note, noteRequired(select.selectedOptions[0]))
    if (message) {
      this.dialogErrorTarget.textContent = message
      this.dialogErrorTarget.classList.remove("hidden")
      return
    }
    this.finishReason({ lost_reason_id: select.value, lost_note: note.trim() })
  }

  closeDialog() {
    this.finishReason(null)
  }

  // The dialog closed by Escape (or after a choice): a pending choice is a cancel.
  cancelReason() {
    this.finishReason(null)
  }

  finishReason(choice) {
    const resolve = this.pendingReason
    this.pendingReason = null
    if (this.dialogTarget.open) this.dialogTarget.close()
    if (resolve) resolve(choice)
  }

  refreshEmpty() {
    this.columnTargets.forEach((column) => {
      const empty = column.querySelector("[data-column-empty]")
      if (empty) empty.classList.toggle("hidden", column.querySelector("[data-deal-card]") !== null)
    })
  }

  unmark(column) {
    column.classList.remove("ring-2", "ring-brand")
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }

  get csrfToken() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }
}
